import React, { useEffect, useState } from 'react';
import { getRecipients, addRecipient, deleteRecipient, getAlertLog, triggerAlerts } from '../services/api';
import '../App.css';

export default function AlertsPage() {
  const [recipients, setRecipients] = useState([]);
  const [alertLog, setAlertLog]     = useState([]);
  const [email, setEmail]           = useState('');
  const [name, setName]             = useState('');
  const [testing, setTesting]       = useState(false);
  const [toast, setToast]           = useState('');

  const load = () => {
    getRecipients().then(r => setRecipients(r.data));
    getAlertLog().then(r => setAlertLog(r.data));
  };

  useEffect(() => { load(); }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const handleAdd = async () => {
    if (!email.trim()) return;
    await addRecipient({ email: email.trim(), name: name.trim() || null });
    setEmail(''); setName('');
    load();
    showToast('Recipient added');
  };

  const handleDelete = async (id) => {
    await deleteRecipient(id);
    load();
    showToast('Recipient removed');
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await triggerAlerts();
      showToast('Alert check triggered — check email if thresholds are exceeded');
    } catch (e) {
      showToast('Error triggering alerts');
    } finally {
      setTesting(false);
    }
  };

  const alertTypeColor = (t) => {
    if (t?.includes('hard')) return '#dc2626';
    if (t?.includes('soft')) return '#d97706';
    if (t?.includes('expir')) return '#b45309';
    return '#64748b';
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Alerts & Notifications</div>
          <div className="page-sub">Email alerts run daily at 8 AM</div>
        </div>
        <button className="btn btn-primary" onClick={handleTest} disabled={testing}>
          {testing ? '⏳ Running…' : '▶ Run Alert Check Now'}
        </button>
      </div>

      {/* Add recipient */}
      <div className="card">
        <div className="card-title">📧 Alert Email Recipients</div>
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <input className="search-input" placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)} style={{ maxWidth: 260 }} />
          <input className="search-input" placeholder="Name (optional)" value={name} onChange={e => setName(e.target.value)} style={{ maxWidth: 200 }} />
          <button className="btn btn-primary" onClick={handleAdd}>Add Recipient</button>
        </div>

        {recipients.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: 13 }}>No recipients yet. Add at least one email to receive alerts.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr><th style={{ textAlign: 'left', padding: '6px 0', color: '#64748b', fontSize: 11, textTransform: 'uppercase' }}>Name</th><th>Email</th><th></th></tr></thead>
            <tbody>
              {recipients.map(r => (
                <tr key={r.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '8px 0' }}>{r.name || '—'}</td>
                  <td>{r.email}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(r.id)}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Alert rules info */}
      <div className="card">
        <div className="card-title">⚙️ Alert Rules</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 14px', background: '#fef2f2', borderRadius: 8, borderLeft: '4px solid #dc2626' }}>
            <span style={{ fontSize: 20 }}>🔴</span>
            <div><b>Hard Threshold (Critical)</b> — item quantity is at or below the hard threshold. Immediate reorder required.</div>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 14px', background: '#fffbeb', borderRadius: 8, borderLeft: '4px solid #d97706' }}>
            <span style={{ fontSize: 20 }}>🟡</span>
            <div><b>Soft Threshold (Warning)</b> — item quantity is between hard and soft threshold. Plan to reorder soon.</div>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 14px', background: '#fff7ed', borderRadius: 8, borderLeft: '4px solid #b45309' }}>
            <span style={{ fontSize: 20 }}>📅</span>
            <div><b>Expiry Warning</b> — any lot expires within 7 or 30 days, or is already expired.</div>
          </div>
        </div>
      </div>

      {/* Alert log */}
      <div className="card">
        <div className="card-title">📋 Recent Alerts Sent</div>
        {alertLog.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: 13 }}>No alerts have been sent yet.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Time</th><th>Item</th><th>Type</th><th>Sent To</th></tr></thead>
              <tbody>
                {alertLog.map((a, i) => (
                  <tr key={i}>
                    <td style={{ fontSize: 12 }}>{new Date(a.sent_at).toLocaleString()}</td>
                    <td>{a.item_name || '—'}</td>
                    <td><span style={{ fontSize: 11, fontWeight: 700, color: alertTypeColor(a.alert_type) }}>{a.alert_type?.replace(/_/g,' ')}</span></td>
                    <td style={{ fontSize: 12 }}>{a.sent_to}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {toast && (
        <div style={{ position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)', background: '#0f172a', color: 'white', padding: '12px 24px', borderRadius: 50, fontSize: 13, zIndex: 100 }}>
          {toast}
        </div>
      )}
    </div>
  );
}
